import { isEqual } from 'lodash';
import { Euler, Matrix4, Quaternion, Vector3 } from 'three';
import { Point3D } from '../../team-apis/otterdam/answers/shape-typings';
import { CuboidFace } from '../render/mesh/cuboid/cuboid-helper';
import { buildCuboidPointsFromShapeLocation } from '../utils/cuboid-utils';
import {
  eulerToPoint,
  pointToVector,
  TentPoleVertex,
} from '../utils/point-cloud-utils';
import { mapPoint, roundValue, roundVector } from '../utils/point-utils';
import { X, Y, Z } from './point-cloud-content-state';
import { ShapeLocationState3D } from './shape-location-state-3d';

export const CUBE_SIZE = 1;
export const CUBE_MIN_EDGE_LENGTH = 0.01;
export const CUBE_MIN_LEGAL_SCALE = CUBE_MIN_EDGE_LENGTH / CUBE_SIZE;

export interface CuboidLocationState3D extends ShapeLocationState3D {
  position: Point3D;
  rotation: Point3D;
  scale: Point3D;
}

export function isCuboidLocationState3D(
  shapeLocation: ShapeLocationState3D,
): shapeLocation is CuboidLocationState3D {
  return (
    'position' in shapeLocation &&
    'rotation' in shapeLocation &&
    'scale' in shapeLocation
  );
}

export function locationStateExternal(location: CuboidLocationState3D) {
  return {
    frame_number: location.frame_number,
    points: buildCuboidPointsFromShapeLocation(location),
    visibility: location.visibility,
    source: location.source,
  };
}

export function isSameCuboidLocation(
  loc1: CuboidLocationState3D,
  loc2: CuboidLocationState3D,
) {
  return (
    loc1.visibility === loc2.visibility &&
    isEqual(loc1.position, loc2.position) &&
    isEqual(loc1.rotation, loc2.rotation) &&
    isEqual(loc1.scale, loc2.scale)
  );
}

export function calcPositionRotationScale(pts: Point3D[]) {
  const bBottomLeft = pointToVector(pts[TentPoleVertex.BackBottomLeft]);
  const bBottomRight = pointToVector(pts[TentPoleVertex.BackBottomRight]);
  const fBottomLeft = pointToVector(pts[TentPoleVertex.FrontBottomLeft]);
  const bTopLeft = pointToVector(pts[TentPoleVertex.BackTopLeft]);
  const fTopRight = pointToVector(pts[TentPoleVertex.FrontTopRight]);

  const position = getCenter(bBottomLeft, fTopRight);
  const scale = getScale(bBottomLeft, bBottomRight, fBottomLeft, bTopLeft);
  const rotation = getRotation(
    bBottomLeft,
    bBottomRight,
    fBottomLeft,
    bTopLeft,
  );

  return { position, rotation, scale };
}

// eslint-disable-next-line max-lines-per-function,complexity -- a cuboid has 6 faces, hence the 6 case statements
export function rotateSelectedFace(
  location: CuboidLocationState3D,
  face: CuboidFace,
): CuboidLocationState3D {
  let rotation = location.rotation;
  let scale = location.scale;

  switch (face) {
    case CuboidFace.Front:
      // Make it appear as if the current selected face
      // has been rotated by 90° clockwise.
      rotation = rotateCuboid(rotation, Y, Math.PI / 2);
      scale = flipScales(scale, X, Z);
      break;

    case CuboidFace.Back:
      rotation = rotateCuboid(rotation, X, Math.PI);
      rotation = rotateCuboid(rotation, Y, Math.PI);
      break;

    case CuboidFace.Right:
      rotation = rotateCuboid(rotation, Z, -Math.PI / 2);
      scale = flipScales(scale, X, Y);
      break;

    case CuboidFace.Left:
      rotation = rotateCuboid(rotation, Z, Math.PI / 2);
      scale = flipScales(scale, X, Y);
      break;

    case CuboidFace.Top:
      rotation = rotateCuboid(rotation, X, -Math.PI / 2);
      scale = flipScales(scale, Y, Z);
      break;

    case CuboidFace.Bottom:
      rotation = rotateCuboid(rotation, X, Math.PI / 2);
      scale = flipScales(scale, Y, Z);
      break;
  }

  return {
    ...location,
    scale,
    rotation,
  };
}

export function rotateCuboid(rotation: Point3D, axis: number, radians: number) {
  const rotationAxis: Point3D = [0, 0, 0];
  rotationAxis[axis] = 1;
  const rotationQuarternion = new Quaternion().setFromAxisAngle(
    pointToVector(rotationAxis),
    radians,
  );

  const cuboidRotation = new Euler().fromArray(rotation);
  const cuboidQuarternion = new Quaternion().setFromEuler(cuboidRotation);
  cuboidQuarternion.multiply(rotationQuarternion);
  return new Euler()
    .setFromQuaternion(cuboidQuarternion)
    .toArray()
    .splice(0, 3) as Point3D;
}

function flipScales(scale: Point3D, axis1: number, axis2: number) {
  const newScale = scale.slice() as Point3D;
  newScale[axis1] = scale[axis2];
  newScale[axis2] = scale[axis1];
  return newScale;
}

function getCenter(min: Vector3, max: Vector3) {
  return roundVector(new Vector3().addVectors(min, max).multiplyScalar(0.5));
}

function getScale(
  origin: Vector3,
  right: Vector3,
  front: Vector3,
  top: Vector3,
): Point3D {
  const width = right.distanceTo(origin);
  const depth = front.distanceTo(origin);
  const height = top.distanceTo(origin);

  return [
    roundValue(width / CUBE_SIZE),
    roundValue(depth / CUBE_SIZE),
    roundValue(height / CUBE_SIZE),
  ];
}

function getRotation(
  origin: Vector3,
  right: Vector3,
  front: Vector3,
  top: Vector3,
) {
  const matrix = createRotationMatrix(origin, right, front, top);
  const rotation = new Euler();
  rotation.setFromRotationMatrix(matrix);
  return mapPoint(eulerToPoint(rotation), roundValue);
}

function createRotationMatrix(
  origin: Vector3,
  right: Vector3,
  front: Vector3,
  top: Vector3,
) {
  const matrix = new Matrix4();
  const widthUnitVector = new Vector3().subVectors(right, origin).normalize();
  const depthUnitVector = new Vector3().subVectors(origin, front).normalize();
  const heightUnitVector = new Vector3().subVectors(top, origin).normalize();

  matrix.set(
    widthUnitVector.x,
    depthUnitVector.x,
    heightUnitVector.x,
    0,
    widthUnitVector.y,
    depthUnitVector.y,
    heightUnitVector.y,
    0,
    widthUnitVector.z,
    depthUnitVector.z,
    heightUnitVector.z,
    0,
    0,
    0,
    0,
    1,
  );

  return matrix;
}
